import React, { useState, useRef, useEffect } from 'react';
import { Link, Switch, Route, useHistory, useParams, useLocation, useRouteMatch } from 'react-router-dom';
import { clone } from 'ramda';
import isMobilePhone from 'validator/lib/isMobilePhone';
import isEmail from 'validator/lib/isEmail';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Cropper } from 'react-cropper';
import {
  PopupMenu,
  TabsContainer,
  TabItem,
  Drawer,
  TextInputWithLabel,
  DatePicker,
  Checkbox,
  FileUploader,
  Loader,
  Button
} from 'quantux-ui';
import WarningDialog from '../warning-dialog';
import { gray, salmon, blue, theme } from '../../theme';
import { BASE_URL, DRAWER_BREAK_POINT, titleCase, padZero } from '../../utils';
import PatientInsurance from './patient-insurance';
import PatientInfo from './patient-info';
import PatientContact from './patient-contact';

const TODAY = new Date();
const NAVBAR_HEIGHT = 70;
const drawerWidth = window.innerWidth <= DRAWER_BREAK_POINT ? window.innerWidth : 320;
const tabsTextStyles = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const flagIcon = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.997 0H0v16h16V0h-.003Zm0 0v16l-5.622-8 5.622-8Z" fill="#FF6060"
    />
  </svg>
);

const userIcon = (
  <svg
    width="20"
    height="20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2 0a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm12.063 5.938a3.745 3.745 0 0 1-3.75 3.75 3.745 3.745 0 0 1-3.75-3.75 3.745 3.745 0 0 1 3.75-3.75 3.745 3.745 0 0 1 3.75 3.75Zm-11.25 10c0-2.5 5-3.876 7.5-3.876s7.5 1.376 7.5 3.876v1.25h-15v-1.25Z"
      fill="#96C8FF"
    />
  </svg>
);

const idIcon = (
  <svg
    width="20"
    height="15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.406 0h17.188C19.37 0 20 .629 20 1.406v12.188C20 14.37 19.371 15 18.594 15H1.406A1.405 1.405 0 0 1 0 13.594V1.406C0 .63.629 0 1.406 0ZM8.75 11.094c0 .777-.629 1.406-1.406 1.406H3.906A1.405 1.405 0 0 1 2.5 11.094V9.375c0-1.078.545-2.027 1.375-2.59a2.5 2.5 0 1 1 3.5 0 3.122 3.122 0 0 1 1.375 2.59v1.719ZM6.719 5a1.094 1.094 0 1 0-2.19.001A1.094 1.094 0 0 0 6.72 5ZM3.906 9.473c0-.006 0-.012.002-.02-.002-.025-.002-.053-.002-.078a1.719 1.719 0 0 1 3.438 0c0 .027 0 .053-.002.078.002.006.002.012.002.02v1.523a.097.097 0 0 1-.098.098H4.004a.097.097 0 0 1-.098-.098V9.473Zm14.688 4.12H1.406V1.407h17.188v12.188Zm-3.047-5.39h-4.844a.703.703 0 1 1 0-1.406h4.844a.703.703 0 1 1 0 1.406Zm-4.844 3.203h3.594a.703.703 0 1 0 0-1.406h-3.594a.703.703 0 1 0 0 1.406ZM16.797 5h-6.094a.703.703 0 1 1 0-1.406h6.094a.703.703 0 1 1 0 1.406Z"
      fill="#9CF"
    />
  </svg>
);

const insuranceIcon = (
  <svg
    width="18"
    height="20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M.5 2.857 9.071 0l8.572 2.857v8.572A8.57 8.57 0 0 1 9.07 20 8.57 8.57 0 0 1 .5 11.429V2.857Zm8.574 15.536a6.965 6.965 0 0 0 6.964-6.964V4.063L9.074 1.607 2.109 4.063v7.366a6.965 6.965 0 0 0 6.965 6.964Z"
      fill="#00A0FF"
    />
  </svg>
);

const contactIcon = (
  <svg
    width="21"
    height="20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.482 6.83H.804a.803.803 0 1 1 0-1.607h2.678a.803.803 0 1 1 0 1.607Zm0 6.383a.803.803 0 1 1 0 1.607H.804a.803.803 0 1 1 0-1.607h2.678Z"
      fill="#96C8FF"
    />
    <path
      d="M18.394 0H3.037C2.148 0 1.43.719 1.43 1.607v1.25a.803.803 0 1 0 1.607 0v-1.25h15.357v16.786H3.037v-1.206h-.002l.002-.044a.803.803 0 1 0-1.605.044H1.43v1.206c0 .888.718 1.607 1.607 1.607h15.357c.888 0 1.607-.719 1.607-1.607V1.607C20.001.72 19.282 0 18.394 0Z"
      fill="#96C8FF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.422 11.607a3.666 3.666 0 0 1-3.66-3.661 3.666 3.666 0 0 1 3.66-3.66 3.666 3.666 0 0 1 3.661 3.66 3.666 3.666 0 0 1-3.66 3.66ZM.804 10.826h2.678a.803.803 0 1 0 0-1.607H.804a.803.803 0 1 0 0 1.607Zm8.563-2.88c0-1.132.922-2.053 2.054-2.053 1.132 0 2.053.921 2.053 2.053A2.057 2.057 0 0 1 11.421 10a2.057 2.057 0 0 1-2.054-2.054Z"
      fill="#96C8FF"
    />
    <path
      d="M15.63 15.714a.8.8 0 0 1-.788-.654 3.485 3.485 0 0 0-6.843 0 .804.804 0 0 1-1.579-.299 5.09 5.09 0 0 1 10 0 .804.804 0 0 1-.79.953Z"
      fill="#96C8FF"
    />
  </svg>
);

const PatientDashboard = () => {
  const [isDrawerOpen, toggleDrawer] = useState(true);
  const [isPopupMenuOpen, togglePopupMenu] = useState(false);
  const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
  const [showEditProfileDrawer, setShowEditProfileDrawer] = useState(false);
  const [activePatient, setActivePatient] = useState({});
  const [patientData, setPatientData] = useState({});
  // #region Patient Profile
  const [showDrawerCloseWarning, setShowDrawerCloseWarning] = useState(false);
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientMiddleName, setPatientMiddleName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientSSN1, setPatientSSN1] = useState('');
  const [patientSSN2, setPatientSSN2] = useState('');
  const [patientSSN3, setPatientSSN3] = useState('');
  const [isPatientDobPristine, setIsPatientDobPristine] = useState(true);
  const [showPatientDobAlert, setShowPatientDobAlert] = useState(true);
  const [patientDobMonth, setPatientDobMonth] = useState(TODAY.getMonth());
  const [patientDobDate, setPatientDobDate] = useState(TODAY.getDate());
  const [patientDobYear, setPatientDobYear] = useState(TODAY.getFullYear());
  const [patientDisplayName, setPatientDisplayName] = useState('');
  const [isPatientDisplayNamePristine, setIsPatientDisplayNamePristine] = useState(true);
  const [isPatientDisplayNameValid, setIsPatientDisplayNameValid] = useState(true);
  const [patientUserName, setPatientUserName] = useState('');
  const [isPatientUserNamePristine, setIsPatientUserNamePristine] = useState(true);
  const [isPatientUserNameDuplicate, setIsPatientUserNameDuplicate] = useState(false);
  const [isPatientUserNameValid, setIsPatientUserNameValid] = useState(true);
  // Home Address
  const [patientHomeStreetAddress, setPatientHomeStreetAddress] = useState('');
  const [patientHomeSuite, setPatientHomeSuite] = useState('');
  const [patientHomeCity, setPatientHomeCity] = useState('');
  const [patientHomeState, setPatientHomeState] = useState('');
  const [patientHomeZip, setPatientHomeZip] = useState('');
  // Mailing Address
  const [isPatientMailingAddressSameAsHomeAddress, setIsPatientMailingAddressSameAsHomeAddress] = useState(false);
  const [patientMailingStreetAddress, setPatientMailingStreetAddress] = useState('');
  const [patientMailingSuite, setPatientMailingSuite] = useState('');
  const [patientMailingCity, setPatientMailingCity] = useState('');
  const [patientMailingState, setPatientMailingState] = useState('');
  const [patientMailingZip, setPatientMailingZip] = useState('');
  // Mobile, Phone and Email
  const [patientMobile, setPatientMobile] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientContactType, setPatientContactType] = useState('mobile');
  const [patientEmail, setPatientEmail] = useState('');
  const [canUpdatePatient, setCanUpdatePatient] = useState(false);
  // Flags
  const [piFlag, setPiFlag] = useState(false);
  const [siFlag, setSiFlag] = useState(false);
  const [tiFlag, setTiFlag] = useState(false);
  const [patientInfoFlag, setPatientInfoFlag] = useState(false);
  const [contactPersonFlag, setContactPersonFlag] = useState(false);
  // #endregion

  // #region Avatar Cropper Drawer
  const [showPatientAvatarCropperDrawer, togglePatientAvatarCropperDrawer] = useState(false);
  const [patientAvatarImageUrl, setPatientAvatarImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
  const [showPatientAvatarCropper, setShowPatientAvatarCropper] = useState(false);
  const [isPatientAvatarUploading, setIsPatientAvatarUploading] = useState(false);
  // #endregion

  // #region Front ID card
  const [showFrontCropperDrawer, toggleFrontCropperDrawer] = useState(false);
  const [patientFrontIdImageUrl, setPatientFrontIdImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
  const [showFrontCropper, setShowFrontCropper] = useState(false);
  const [isFrontUploading, setIsFrontUploading] = useState(false);
  const [showFrontIdLoader, setShowFrontIdLoader] = useState(false);
  // #endregion

  // #region Back ID card
  const [showBackCropperDrawer, toggleBackCropperDrawer] = useState(false);
  const [patientBackIdImageUrl, setPatientBackIdImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
  const [showBackCropper, setShowBackCropper] = useState(false);
  const [isBackUploading, setIsBackUploading] = useState(false);
  const [showBackIdLoader, setShowBackIdLoader] = useState(false);
  // #endregion

  // Refs
  const mainTabsContainer = useRef();
  const popupRef = useRef();
  const contactRef = useRef();
  const drawerAvatarRef = useRef();
  const patientAvatarCropperRef = useRef();
  const patientAvatarFileInputRef = useRef();
  // Front ID refs
  const frontCardRef = useRef();
  const frontCropperRef = useRef();
  const frontFileInputRef = useRef();
  // Back ID refs
  const backCardRef = useRef();
  const backCropperRef = useRef();
  const backFileInputRef = useRef();

  // Router related
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const { path, url } = useRouteMatch();
  console.log(`path = ${path}`);
  const { patientId } = useParams();
  console.log(`patientId = ${patientId}`);

  const determinePiFlag = (piData, isNoPrimaryInsurance = false) => {
    if (isNoPrimaryInsurance) {
      setPiFlag(false);
      return;
    }
    if (Object.keys(piData).length > 0) {
      const {
        memberFirstName = '',
        memberLastName = '',
        patientRelationToMember = '',
        policyNumber = '',
        authorizationNumber = '',
        groupNumber = '',
        memberDob
      } = piData;
      console.log('------------------------------------');
      console.log(piData);
      console.log('------------------------------------');
      const nameFlag = memberFirstName.length >= 1 && memberLastName.length >= 1;
      // const dobFlag = memberDob && !isNaN(Date.parse(memberDob));
      console.log(`memberDob = ${memberDob}`);
      const dobFlag = !isNaN(Date.parse(memberDob)) && memberDob;
      const relationFlag = patientRelationToMember.length > 0;
      const policyFlag = policyNumber.length > 0;
      const authFlag = authorizationNumber.length > 0;
      const groupFlag = groupNumber.length > 0;
      console.log(`nameFlag = ${nameFlag}`);
      console.log(`dobFlag = ${dobFlag}`);
      console.log(`relationFlag = ${relationFlag}`);
      console.log(`policyFlag = ${policyFlag}`);
      console.log(`authFlag = ${authFlag}`);
      console.log(`groupFlag = ${groupFlag}`);
      setPiFlag(!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
    } else {
      setPiFlag(true);
    }
  };

  const determineSiFlag = (siData, isNoSecondaryInsurance = false) => {
    if (isNoSecondaryInsurance) {
      setSiFlag(false);
      return;
    }
    if (Object.keys(siData).length > 0) {
      const {
        memberFirstName = '',
        memberLastName = '',
        memberDob,
        patientRelationToMember = '',
        policyNumber = '',
        authorizationNumber = '',
        groupNumber = ''
      } = siData;
      const nameFlag = memberFirstName.length >= 1 && memberLastName.length >= 1;
      const dobFlag = memberDob && !isNaN(Date.parse(memberDob));
      const relationFlag = patientRelationToMember.length > 0;
      const policyFlag = policyNumber.length > 0;
      const authFlag = authorizationNumber.length > 0;
      const groupFlag = groupNumber.length > 0;
      setSiFlag(!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
    } else {
      setSiFlag(true);
    }
  };

  const determineTiFlag = (tiData, isNoTertiaryInsurance = false) => {
    if (isNoTertiaryInsurance) {
      setTiFlag(false);
      return;
    }
    if (Object.keys(tiData).length > 0) {
      const {
        memberFirstName = '',
        memberLastName = '',
        memberDob,
        patientRelationToMember = '',
        policyNumber = '',
        authorizationNumber = '',
        groupNumber = ''
      } = tiData;
      const nameFlag = memberFirstName.length >= 1 && memberLastName.length >= 1;
      const dobFlag = memberDob && !isNaN(Date.parse(memberDob));
      const relationFlag = patientRelationToMember.length > 0;
      const policyFlag = policyNumber.length > 0;
      const authFlag = authorizationNumber.length > 0;
      const groupFlag = groupNumber.length > 0;
      setTiFlag(!(nameFlag && dobFlag && relationFlag && policyFlag && authFlag && groupFlag));
    } else {
      setTiFlag(true);
    }
  };

  const determinePatientInfoFlag = (patientInfo) => {
    const MIN_STREET_ADDRESS_LENGTH = 5;
    const MIN_CITY_LENGTH = 3;
    const MIN_STATE_LENGTH = 2;
    const MIN_ZIP_LENGTH = 5;

    const {
      firstName = '',
      lastName = '',
      ssn1 = '',
      ssn2 = '',
      ssn3 = '',
      displayName = '',
      userName = '',
      mobile = '',
      email = '',
      homeAddress = {},
      mailingAddress = {}
    } = patientInfo;

    const {
      streetAddress1: homeStreetAddress = '',
      city: homeCity = '',
      state: homeState = '',
      zip: homeZip
    } = homeAddress;

    const {
      streetAddress1: mailingStreetAddress = '',
      city: mailingCity = '',
      state: mailingState = '',
      zip: mailingZip
    } = mailingAddress;

    // console.log(`firstName = ${firstName}`);
    // console.log(`lastName = ${lastName}`);
    // console.log(`ssn1 = ${ssn1}`);
    // console.log(`ssn2 = ${ssn2}`);
    // console.log(`ssn3 = ${ssn3}`);
    // console.log(`displayName = ${displayName}`);
    // console.log(`userName = ${userName}`);
    // console.log(`mobile = ${mobile}`);
    // console.log(`email = ${email}`);

    // console.log(`homeStreetAddress = ${homeStreetAddress}`);
    // console.log(`homeCity = ${homeCity}`);
    // console.log(`homeState = ${homeState}`);
    // console.log(`homeZip = ${homeZip}`);

    // console.log(`mailingStreetAddress = ${mailingStreetAddress}`);
    // console.log(`mailingCity = ${mailingCity}`);
    // console.log(`mailingState = ${mailingState}`);
    // console.log(`mailingZip = ${mailingZip}`);

    const nameFlag = firstName.length >= 1 && lastName.length >= 1;
    const ssnFlag = ssn1.length === 3 && ssn2.length === 2 && ssn3.length === 4;
    const patientDisplayNameFlag = displayName.length >= 2 && displayName.length <= 40;
    const patientUserNameFlag = userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0;
    const patientHomeAddressFlag =
      homeStreetAddress.length >= MIN_STREET_ADDRESS_LENGTH &&
      homeCity.length >= MIN_CITY_LENGTH &&
      homeState.length >= MIN_STATE_LENGTH &&
      homeZip.length >= MIN_ZIP_LENGTH;
    const patientMailingAddressFlag = isPatientMailingAddressSameAsHomeAddress || (
      mailingStreetAddress.length >= MIN_STREET_ADDRESS_LENGTH &&
      mailingCity.length >= MIN_CITY_LENGTH &&
      mailingState.length >= MIN_STATE_LENGTH &&
      mailingZip.length >= MIN_ZIP_LENGTH
    );
    const mobileFlag = isMobilePhone(mobile, 'en-US');
    const emailFlag = isEmail(email);

    // console.log(`nameFlag = ${nameFlag}`);
    // console.log(`ssnFlag = ${ssnFlag}`);
    // console.log(`patientDisplayNameFlag = ${patientDisplayNameFlag}`);
    // console.log(`patientUserNameFlag = ${patientUserNameFlag}`);
    // console.log(`patientHomeAddressFlag = ${patientHomeAddressFlag}`);
    // console.log(`patientMailingAddressFlag = ${patientMailingAddressFlag}`);
    // console.log(`mobileFlag = ${mobileFlag}`);
    // console.log(`emailFlag = ${emailFlag}`);

    setPatientInfoFlag(!(nameFlag &&
      ssnFlag &&
      patientDisplayNameFlag &&
      patientUserNameFlag &&
      // patientHomeAddressFlag &&
      // patientMailingAddressFlag &&
      mobileFlag &&
      emailFlag));
  };

  const determineContactPersonFlag = (patientInfo) => {
    let flagValue = true;
    if (patientInfo.contactPersons && Array.isArray(patientInfo.contactPersons) && patientInfo.contactPersons.length > 0) {
      for (let i = 0; i < patientInfo.contactPersons.length; i++) {
        const cp = patientInfo.contactPersons[i];
        const firstNameFlag = cp.contactPersonFirstName && cp.contactPersonFirstName.length >= 1;
        const lastNameFlag = cp.contactPersonLastName && cp.contactPersonLastName.length >= 1;
        const relationFlag = cp.contactPersonRelationToPatient && cp.contactPersonRelationToPatient.length >= 4;
        const contactNumberFlag = cp.contactPersonContactNumber && cp.contactPersonContactNumber.length >= 10;
        flagValue = !(firstNameFlag && lastNameFlag && relationFlag && contactNumberFlag);
        if (!flagValue) {
          break;
        }
      }
      setContactPersonFlag(flagValue);
    } else {
      setContactPersonFlag(true);
    }
  };

  const loadPatientData = () => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      headers: reqHeaders,
      redirect: 'follow'
    };

    const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
    const params = { id: patientId };
    url.search = new URLSearchParams(params).toString();

    fetch(url, requestOptions)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        console.log(reqData.patient);
        setActivePatient(reqData.patient);
        setPatientData(clone(reqData.patient));
        determinePiFlag(reqData.patient.primaryInsurance || {}, reqData.patient.isNoPrimaryInsurance);
        determineSiFlag(reqData.patient.secondaryInsurance || {}, reqData.patient.isNoSecondaryInsurance);
        determineTiFlag(reqData.patient.tertiaryInsurance || {}, reqData.patient.isNoTertiaryInsurance);
        determinePatientInfoFlag(reqData.patient);
        determineContactPersonFlag(reqData.patient)
      })
      .catch(error => console.log('error', error));
  };

  // Use Effects
  useEffect(() => {
    loadPatientData();
  }, []);

  // Duplicate Username
  useEffect(() => {
    if (patientUserName === activePatient.userName) {
      setIsPatientUserNameDuplicate(false);
      console.log('isPatientUserNameDuplicate = false');
    } else if (patientUserName.length >= 2 && patientUserName.length <= 20 && patientUserName.indexOf(' ') < 0) {
      const url = new URL(`${BASE_URL}/patients/checkDuplicateUserName`);
      const params = { userName: patientUserName };
      url.search = new URLSearchParams(params).toString();

      fetch(url)
        .then(response => response.text())
        .then(result => {
          const reqData = JSON.parse(result);
          if (reqData.success) {
            setIsPatientUserNameDuplicate(reqData.isDuplicate);
            console.log(`isPatientUserNameDuplicate = ${reqData.isDuplicate}`);
          } else {
            setIsPatientUserNameDuplicate(true);
            console.log('isPatientUserNameDuplicate = true');
          }
        })
        .catch(error => console.log('error', error));
    }
  }, [patientUserName]);

  // Reset Mailing Address when mailing addresset set to not same as home address
  useEffect(() => {
    if (isPatientMailingAddressSameAsHomeAddress) {
      setPatientMailingStreetAddress('');
      setPatientMailingSuite('');
      setPatientMailingCity('');
      setPatientMailingState('');
      setPatientMailingZip('');
    }
  }, [isPatientMailingAddressSameAsHomeAddress]);

  // canUpdatePatient - Effect
  useEffect(() => {
    const patientDisplayNameFlag = patientDisplayName.length >= 2 && patientDisplayName.length <= 40;
    const patientUserNameFlag = patientUserName.length >= 2 && patientUserName.length <= 20 && patientUserName.indexOf(' ') < 0 && !isPatientUserNameDuplicate;
    setCanUpdatePatient(patientDisplayNameFlag && patientUserNameFlag);
  }, [
    patientDisplayName,
    isPatientDisplayNamePristine,
    isPatientDisplayNameValid,
    patientUserName,
    isPatientUserNamePristine,
    isPatientUserNameDuplicate,
    isPatientUserNameValid
  ]);

  // Split panel theme
  const {
    drawerBackground,
    drawerBorderColor,
    drawerTextColor,
    drawerFontSize,
    drawerFontWeight,
    // Drawer Tabs
    tabBackground,
    tabActiveBackground,
    tabHoverBackground,
    tabIndicatorBackground,
    tabIndicatorHoverBackground,
    tabIndicatorActiveBackground,
    tabTextColor,
    tabHoverTextColor,
    tabActiveTextColor,
    tabFontSize,
    tabFontWeight,
    groupSeparators,
    headerBackground,
    headerTextColor,
    headerFontSize,
    headerFontWeight,
    groupHeaderTextColor,
    groupHeaderTextOpacity,
    groupHeaderFontSize,
    groupHeaderFontWeight,
    groupTabIndent
  } = theme.splitPanelDrawer;

  // == == == == Styles == == == ==
  const styles = StyleSheet.create({
    dashboardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '32px',
      padding: '0 8px',
      background: gray[500],
      color: gray[300]
    },
    dashboardHeaderIcon: {
      fontSize: '18px',
      color: salmon[500],
      cursor: 'pointer'
    },
    splitPanel: {
      display: 'flex',
      flexFlow: 'row nowrap',
      background: gray[800],
      height: 'calc(100% - 32px)',
      width: '100%',
      overflow: 'hidden',
      fontFamily: 'Lato'
    },
    drawer: {
      position: 'relative',
      height: '100%',
      transition: '0.3s',
      overflowY: 'auto',
      zIndex: '1900',
      borderRight: `1px solid ${gray[700]}`,
      background: gray[800],
      padding: '8px'
    },
    openDrawer: {
      left: '0',
      minWidth: `${drawerWidth}px`,
      width: `${drawerWidth}px`
    },
    hideDrawer: {
      transition: '0.3s',
      width: '0px',
      minWidth: '0px',
      padding: '0',
      left: `calc(-1 * ${drawerWidth}px)`
    },
    drawerHeader: {
      height: '40px',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      cursor: 'pointer',
      background: headerBackground,
      color: headerTextColor,
      borderBottom: `0.5px solid ${drawerBorderColor}`,
      ':hover:not(.activeitem)': {
        background: tabHoverBackground,
        color: tabHoverTextColor
      }
    },
    drawerHeaderActive: {
      color: tabActiveTextColor,
      background: tabActiveBackground
    },
    drawerHeaderIconContainer: {
      height: '32px',
      width: '32px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 4px',
      cursor: 'pointer'
    },
    drawerHeaderIcon: {
      fontSize: '24px',
      cursor: 'pointer'
    },
    drawerHeaderTextContainer: {
      textAlign: 'left',
      lineHeight: '16px',
      cursor: 'pointer'
    },
    drawerHeaderText: {
      textAlign: 'center',
      fontSize: '14px',
      cursor: 'pointer',
      paddingLeft: '4px',
      fontWeight: 'bold'
    },
    drawerHeaderTextSub: {
      fontSize: '12px',
      cursor: 'pointer'
    },
    drawerTabsContainer: {
      height: 'calc(100% - 40px)',
      // height: `${window.innerHeight - 95}px`,
      overflowY: 'auto'
    },
    drawerAccordian: {
      position: 'relative',
      display: 'block',
      transition: '0.5s ease',
      maxHeight: '2000px'
    },
    drawerFile: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '8px',
      color: blue[500],
      height: '40px'
    },
    drawerMenuItem: {
      position: 'relative',
      background: tabBackground,
      color: tabTextColor,
      fill: tabTextColor,
      borderLeft: `1px solid ${tabIndicatorBackground}`,
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      paddingLeft: groupTabIndent,
      ':hover:not(.activeitem)': {
        background: tabHoverBackground,
        color: tabHoverTextColor,
        fill: tabHoverTextColor,
        borderLeft: `1px solid ${tabIndicatorHoverBackground}`
      }
    },
    drawerMenuItemIcon: {
      height: '24px',
      width: '30px',
      minWidth: '24px',
      marginRight: '8px',
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px'
    },
    drawerMenuItemActive: {
      borderLeft: `1px solid ${tabIndicatorActiveBackground}`,
      color: tabActiveTextColor,
      background: tabActiveBackground
    },
    drawerMenuItemText: {
      width: '100%',
      fontSize: '1rem',
      lineHeight: '14px'
    },
    linkStyles: {
      display: 'flex',
      alignItems: 'center',
      color: gray[300]
    },
    selectedLink: {
      color: blue[500]
    },
    menuItem: {
      position: 'relative',
      left: '-3px',
      color: gray[300]
    },
    detail: {
      // position: 'relative',
      transition: '0s',
      height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      width: 'calc(100vw - 320px)',
      left: '0'
    },
    detailExapnd: {
      transition: '0.2s',
      left: 'calc(-1 * 320px)',
      width: '100vw'
    },
    detailHeader: {
      height: '100px',
      background: '#001824',
      display: 'flex',
      padding: '0',
      paddingRight: '8px',
      position: 'relative'
      // borderBottom: `1px solid ${gray[500]}`,
      // background: gray[900],
      // background: gray[500],
      // alignItems: 'center',
    },
    popupMenuContainer: {
      background: '#083050',
      width: '210px',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)'
    },
    popupMenuItem: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#5080B0'
    },
    popupMenuItemSmall: {
      height: '25px',
      display: 'flex',
      fontSize: '14px',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#5080B0',
      ':not(.selected):hover': {
        backgroundColor: '#0066CC',
        color: 'white'
      }
    },
    selectedPopupMenuItemSmall: {
      backgroundColor: '#0066CC',
      color: 'white'
    },
    menuItemIconContainerSmall: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '25px',
      width: '25px'
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '40px',
      width: '40px'
    },
    menuItemIconSmall: {
      fontSize: '14px'
    },
    menuItemIcon: {
      fontSize: '20px'
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    sideDrawerContainer: {
      // background: gray[600],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    sideDrawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    sideDrawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerContentArea: {
      // height: 'calc(100vh - 186px)',
      // height: `${window.innerHeight - 170}px`,
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    sideDrawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    sideDrawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerUserCardText: {
      fontSize: '12px'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    sideDrawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0',
      paddingRight: '8px'
    },
    imageUploaderContainer: {
      position: 'relative',
      maxHeight: '454px'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    sideDrawerLinkStyles: {
      color: gray[300],
      ':visited': {
        color: gray[300]
      }
    },
    sideDrawerSelectedLink: {
      color: blue[500]
    },
    sectionHeading: {
      color: blue[200],
      fontSize: '14px',
      fontWeight: 'bold'
    },
    ssnSeparator: {
      padding: '16px 4px 0 4px'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '4px',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    }
  });

  // #region Drawer Event Handler
  const handleOnClickMiniUserCard = () => { };
  // #endregion

  // #region Edit Profile Drawer Event Handlers
  const openPatientEditProfileDrawer = () => {
    // First, Middle and Last Name
    setPatientFirstName(titleCase(activePatient.firstName) || '');
    setPatientMiddleName(titleCase(activePatient.middleName) || '');
    setPatientLastName(titleCase(activePatient.lastName) || '');
    setPatientFrontIdImageUrl(activePatient.patientIdFrontScan);
    setPatientBackIdImageUrl(activePatient.patientIdBackScan);
    // SSN
    setPatientSSN1(activePatient.ssn1 || '');
    setPatientSSN2(activePatient.ssn2 || '');
    setPatientSSN3(activePatient.ssn3 || '');
    // DOB
    const tmp1 = activePatient.dob.split('T');
    const tmp2 = tmp1[0].split('-');
    const _year = tmp2[0];
    const _month = tmp2[1];
    const _date = tmp2[2];
    setPatientDobDate(+_date);
    setPatientDobMonth((+_month) - 1);
    setPatientDobYear(+_year);

    setIsPatientDobPristine(false);
    // Avatar, Display Name and UserName
    setPatientAvatarImageUrl(activePatient.avatar || 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
    setPatientDisplayName(titleCase(activePatient.displayName));
    setPatientUserName(activePatient.userName || '');
    setIsPatientUserNamePristine(true);
    setIsPatientUserNameDuplicate(false);
    setIsPatientUserNameValid(true);
    setShowPatientDobAlert(false);
    // Home and Mailing Address
    setIsPatientMailingAddressSameAsHomeAddress(activePatient.mailingAddressSameAsHomeAddress);

    if (activePatient.homeAddress) {
      setPatientHomeStreetAddress(activePatient.homeAddress.streetAddress1 || '');
      setPatientHomeSuite(activePatient.homeAddress.suite || '');
      setPatientHomeCity(activePatient.homeAddress.city || '');
      setPatientHomeState(activePatient.homeAddress.state || '');
      setPatientHomeZip(activePatient.homeAddress.zip || '');
    } else {
      setPatientHomeStreetAddress('');
      setPatientHomeSuite('');
      setPatientHomeCity('');
      setPatientHomeState('');
      setPatientHomeZip('');
    }
    if (activePatient.mailingAddress) {
      setPatientMailingStreetAddress(activePatient.mailingAddress.streetAddress1 || '');
      setPatientMailingSuite(activePatient.mailingAddress.suite || '');
      setPatientMailingCity(activePatient.mailingAddress.city || '');
      setPatientMailingState(activePatient.mailingAddress.state || '');
      setPatientMailingZip(activePatient.mailingAddress.zip || '');
    } else {
      setPatientMailingStreetAddress('');
      setPatientMailingSuite('');
      setPatientMailingCity('');
      setPatientMailingState('');
      setPatientMailingZip('');
    }
    setPatientMobile(activePatient.mobile || '');
    // setPatientPhone(activePatient.phone || '');
    setPatientEmail(activePatient.email || '');
    setPatientContactType(activePatient.contactType || 'mobile');
    setShowEditProfileDrawer(true);
  };

  const closeEditProfileDrawer = () => {
    setShowEditProfileDrawer(false);
  };

  const handleOnEditPatientFirstName = (e) => {
    const { value } = e.target;
    setPatientFirstName(value);
  };

  const handleOnEditPatientMiddleName = (e) => {
    const { value } = e.target;
    setPatientMiddleName(value);
  };

  const handleOnEditPatientLastName = (e) => {
    const { value } = e.target;
    setPatientLastName(value);
  };

  const handleOnEditPatientSSN1 = (e) => {
    const { value } = e.target;
    const val = parseInt(value) + '';
    if (!isNaN(val)) {
      if (val.length > 3) {
        setPatientSSN1(val.substring(0, 3));
      } else {
        setPatientSSN1(val);
      }
    } else {
      setPatientSSN1('');
    }
  };

  const handleOnEditPatientSSN2 = (e) => {
    const { value } = e.target;
    const val = parseInt(value) + '';
    if (!isNaN(val)) {
      if (val.length > 2) {
        setPatientSSN2(val.substring(0, 2));
      } else {
        setPatientSSN2(val);
      }
    } else {
      setPatientSSN2('');
    }
  };

  const handleOnEditPatientSSN3 = (e) => {
    const { value } = e.target;
    const val = parseInt(value) + '';
    if (!isNaN(val)) {
      if (val.length > 4) {
        setPatientSSN3(val.substring(0, 4));
      } else {
        setPatientSSN3(val);
      }
    } else {
      setPatientSSN3('');
    }
  };

  const handleOnEditPatientDisplayName = (e) => {
    const { value } = e.target;
    setPatientDisplayName(value);
    setIsPatientDisplayNamePristine(false);
    setIsPatientDisplayNameValid(value.length >= 2 && value.length <= 40);
  };

  const handleOnEditPatientUserName = (e) => {
    const { value } = e.target;
    setPatientUserName(value);
    setIsPatientUserNamePristine(false);
    setIsPatientUserNameValid(value.length >= 2 && value.length <= 20 && value.indexOf(' ') < 0);
  };

  const handleOnEditPatientHomeStreetAddress = (e) => {
    const { value } = e.target;
    setPatientHomeStreetAddress(value);
  };

  const handleOnEditPatientHomeSuite = (e) => {
    const { value } = e.target;
    setPatientHomeSuite(value);
  };

  const handleOnEditPatientHomeCity = (e) => {
    const { value } = e.target;
    setPatientHomeCity(value);
  };

  const handleOnEditPatientHomeState = (e) => {
    const { value } = e.target;
    setPatientHomeState(value);
  };

  const handleOnEditPatientHomeZip = (e) => {
    const { value } = e.target;
    setPatientHomeZip(value);
  };

  const handleOnEditPatientMailingStreetAddress = (e) => {
    const { value } = e.target;
    setPatientMailingStreetAddress(value);
  };

  const handleOnEditPatientMailingSuite = (e) => {
    const { value } = e.target;
    setPatientMailingSuite(value);
  };

  const handleOnEditPatientMailingCity = (e) => {
    const { value } = e.target;
    setPatientMailingCity(value);
  };

  const handleOnEditPatientMailingState = (e) => {
    const { value } = e.target;
    setPatientMailingState(value);
  };

  const handleOnEditPatientMailingZip = (e) => {
    const { value } = e.target;
    setPatientMailingZip(value);
  };

  const handleOnEditPatientMobile = (e) => {
    const { value } = e.target;
    setPatientMobile(value);
  };

  const handleOnEditPatientPhone = (e) => {
    const { value } = e.target;
    setPatientPhone(value);
  };

  const handleOnEditPatientEmail = (e) => {
    const { value } = e.target;
    setPatientEmail(value);
  };

  const handleOnClickSavePatientProfile = () => {
    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('firstName', patientFirstName);
    formData.append('middleName', patientMiddleName);
    formData.append('lastName', patientLastName);
    //
    formData.append('ssn1', patientSSN1);
    formData.append('ssn2', patientSSN2);
    formData.append('ssn3', patientSSN3);
    //
    // const dobIsoString = new Date(dobYear, dobMonth, dobDate).toISOString();
    // formData.append('dob', `${patientDobMonth + 1}/${patientDobDate}/${patientDobYear}`);
    // const dobIsoString = new Date(patientDobYear, patientDobMonth, patientDobDate).toISOString();
    // formData.append('dob', dobIsoString);
    formData.append('dob', `${patientDobYear}-${padZero(patientDobMonth + 1)}-${padZero(patientDobDate)}` + 'T00:00:00.000Z');
    //
    formData.append('displayName', patientDisplayName);
    formData.append('userName', patientUserName);
    //
    formData.append('homeStreetAddress', patientHomeStreetAddress);
    formData.append('homeSuite', patientHomeSuite);
    formData.append('homeCity', patientHomeCity);
    formData.append('homeState', patientHomeState);
    formData.append('homeZip', patientHomeZip);
    //
    formData.append('mailingAddressSameAsHomeAddress', isPatientMailingAddressSameAsHomeAddress);
    if (!isPatientMailingAddressSameAsHomeAddress) {
      formData.append('mailingStreetAddress', patientMailingStreetAddress);
      formData.append('mailingSuite', patientMailingSuite);
      formData.append('mailingCity', patientMailingCity);
      formData.append('mailingState', patientMailingState);
      formData.append('mailingZip', patientMailingZip);
    }
    //
    formData.append('mobile', patientMobile);
    formData.append('contactType', patientContactType);
    // formData.append('phone', patientPhone);
    formData.append('email', patientEmail);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/updatePatientProfile`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          const dd = new Date(patientDobYear, patientDobMonth, patientDobDate);
          if (!isPatientMailingAddressSameAsHomeAddress) {
            setActivePatient((prvState) => ({
              ...prvState,
              firstName: patientFirstName,
              middleName: patientMiddleName,
              lastName: patientLastName,
              dob: dd.toISOString(),
              ssn1: patientSSN1,
              ssn2: patientSSN2,
              ssn3: patientSSN3,
              displayName: patientDisplayName,
              userName: patientUserName,
              homeAddress: {
                streetAddress1: patientHomeStreetAddress,
                suite: patientHomeSuite,
                city: patientHomeCity,
                state: patientHomeState,
                zip: patientHomeZip
              },
              mailingAddressSameAsHomeAddress: isPatientMailingAddressSameAsHomeAddress,
              mailingAddress: {
                streetAddress1: patientMailingStreetAddress,
                suite: patientMailingSuite,
                city: patientMailingCity,
                state: patientMailingState,
                zip: patientMailingZip
              },
              mobile: patientMobile,
              contactType: patientContactType,
              // phone: patientPhone,
              email: patientEmail,
              patientIdFrontScan: patientFrontIdImageUrl,
              patientIdBackScan: patientBackIdImageUrl
            }));
            setPatientData((prvState) => ({
              ...prvState,
              firstName: patientFirstName,
              middleName: patientMiddleName,
              lastName: patientLastName,
              dob: dd.toISOString(),
              ssn1: patientSSN1,
              ssn2: patientSSN2,
              ssn3: patientSSN3,
              displayName: patientDisplayName,
              userName: patientUserName,
              homeAddress: {
                streetAddress1: patientHomeStreetAddress,
                suite: patientHomeSuite,
                city: patientHomeCity,
                state: patientHomeState,
                zip: patientHomeZip
              },
              mailingAddressSameAsHomeAddress: isPatientMailingAddressSameAsHomeAddress,
              mailingAddress: {
                streetAddress1: patientMailingStreetAddress,
                suite: patientMailingSuite,
                city: patientMailingCity,
                state: patientMailingState,
                zip: patientMailingZip
              },
              mobile: patientMobile,
              contactType: patientContactType,
              // phone: patientPhone,
              email: patientEmail,
              patientIdFrontScan: patientFrontIdImageUrl,
              patientIdBackScan: patientBackIdImageUrl
            }));
          } else {
            setActivePatient((prvState) => ({
              ...prvState,
              firstName: patientFirstName,
              middleName: patientMiddleName,
              lastName: patientLastName,
              dob: dd.toISOString(),
              ssn1: patientSSN1,
              ssn2: patientSSN2,
              ssn3: patientSSN3,
              displayName: patientDisplayName,
              userName: patientUserName,
              homeStreetAddress: patientHomeStreetAddress,
              homeSuite: patientHomeSuite,
              homeCity: patientHomeCity,
              homeState: patientHomeState,
              homeZip: patientHomeZip,
              mailingAddressSameAsHomeAddress: isPatientMailingAddressSameAsHomeAddress,
              mobile: patientMobile,
              contactType: patientContactType,
              // phone: patientPhone,
              email: patientEmail,
              patientIdFrontScan: patientFrontIdImageUrl,
              patientIdBackScan: patientBackIdImageUrl
            }));
            setPatientData((prvState) => ({
              ...prvState,
              firstName: patientFirstName,
              middleName: patientMiddleName,
              lastName: patientLastName,
              dob: dd.toISOString(),
              ssn1: patientSSN1,
              ssn2: patientSSN2,
              ssn3: patientSSN3,
              displayName: patientDisplayName,
              userName: patientUserName,
              homeStreetAddress: patientHomeStreetAddress,
              homeSuite: patientHomeSuite,
              homeCity: patientHomeCity,
              homeState: patientHomeState,
              homeZip: patientHomeZip,
              mailingAddressSameAsHomeAddress: isPatientMailingAddressSameAsHomeAddress,
              mobile: patientMobile,
              contactType: patientContactType,
              // phone: patientPhone,
              email: patientEmail,
              patientIdFrontScan: patientFrontIdImageUrl,
              patientIdBackScan: patientBackIdImageUrl
            }));
          }
          closeEditProfileDrawer();
          loadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnChangePatientContactType = (val) => {
    setPatientContactType(val);
  };
  // #endregion

  // #region Avatar Cropper Drawer Handlers
  const closePatientAvatarCropperDrawer = () => {
    togglePatientAvatarCropperDrawer(false);
    setShowPatientAvatarCropper(false);
    setIsPatientAvatarUploading(false);
  };

  const uploadPatientAvatar = () => {
    const formData = new FormData();
    const img = patientAvatarFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Avatar file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPatientAvatarImageUrl(res.fileUrl);
          setIsPatientAvatarUploading(false);
          setShowPatientAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPatientAvatarFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsPatientAvatarUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Avatar file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPatientAvatarImageUrl(res.fileUrl);
          setIsPatientAvatarUploading(false);
          setShowPatientAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedAvatar = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientId);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updatePatientAvatar`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped avatar image url', res.fileUrl);
          // props.reloadPatientDataData(res.usr);
          setPatientAvatarImageUrl(res.fileUrl);
          setActivePatient((prvState) => ({
            ...prvState,
            avatar: res.fileUrl
          }));
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPatientAvatarFileSelected = () => {
    if (patientAvatarFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', patientAvatarFileInputRef.current.files[0]);
      console.log('typeof img', typeof patientAvatarFileInputRef.current.files[0]);
      setIsPatientAvatarUploading(true);
      uploadPatientAvatar();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickPatientAvatarCrop = () => {
    const imageElement = patientAvatarCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerAvatarRef.current.src = url;
      uploadCroppedAvatar(blob);
      closePatientAvatarCropperDrawer();
    });
  };
  // #endregion

  // #region Front ID Cropper drawer
  const closeFrontCropperDrawer = () => {
    toggleFrontCropperDrawer(false);
    setShowFrontCropper(false);
    setIsFrontUploading(false);
  };

  const uploadFront = () => {
    const formData = new FormData();
    const img = frontFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading patient front ID for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Patient ID file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPatientFrontIdImageUrl(res.fileUrl);
          setIsFrontUploading(false);
          setShowFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFrontFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsFrontUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading patient id front.');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Patient ID front for cropper uploaded!');
        console.log(res);
        if (res.success) {
          // setPiFrontImageUrl(res.fileUrl);
          setPatientFrontIdImageUrl(res.fileUrl);
          setIsFrontUploading(false);
          setShowFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedFront = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientId);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updatePatientIdFront`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped front image url', res.fileUrl);
          setPatientFrontIdImageUrl(res.fileUrl);
          setShowFrontIdLoader(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFrontFileSelected = () => {
    if (frontFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', frontFileInputRef.current.files[0]);
      console.log('typeof img', typeof frontFileInputRef.current.files[0]);
      setIsFrontUploading(true);
      uploadFront();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickFrontCrop = () => {
    const imageElement = frontCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      frontCardRef.current.src = url;
      setShowFrontIdLoader(true);
      uploadCroppedFront(blob);
      closeFrontCropperDrawer();
    });
  };
  // #endregion

  // #region Back ID Cropper drawer
  const closeBackCropperDrawer = () => {
    toggleBackCropperDrawer(false);
    setShowBackCropper(false);
    setIsBackUploading(false);
  };

  const uploadBack = () => {
    const formData = new FormData();
    const img = backFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading patient back ID for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Patient ID file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPatientBackIdImageUrl(res.fileUrl);
          setIsBackUploading(false);
          setShowBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBackFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsBackUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading patient id back.');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Patient ID back for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPatientBackIdImageUrl(res.fileUrl);
          setIsBackUploading(false);
          setShowBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedBack = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientId);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updatePatientIdBack`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped back image url', res.fileUrl);
          setPatientBackIdImageUrl(res.fileUrl);
          setShowBackIdLoader(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBackFileSelected = () => {
    if (backFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', backFileInputRef.current.files[0]);
      console.log('typeof img', typeof backFileInputRef.current.files[0]);
      setIsBackUploading(true);
      uploadBack();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickBackCrop = () => {
    const imageElement = backCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      backCardRef.current.src = url;
      setShowBackIdLoader(true);
      uploadCroppedBack(blob);
      closeBackCropperDrawer();
    });
  };
  // #endregion

  // #region Props Handlers
  const handleOnUpdateFlag = () => {
    loadPatientData();
  };
  // #endregion

  return (
    <div style={{ height: 'calc(100% - 40px)' }}>
      {/* Dashboard Header */}
      <div className={css(styles.dashboardHeader)}>
        <div>User Profile</div>
        <div
          className={css(styles.dashboardHeaderIcon)}
          onClick={() => {
            // history.push(`${url}/admin/patients`);
            history.push('/admin/patients');
          }}
        >
          <i className='fa fa-times-circle' />
        </div>
      </div>
      {/* Patient Profile Drawer Closing Warning */}
      {showDrawerCloseWarning &&
        <WarningDialog
          show={showDrawerCloseWarning}
          title='Close Patient Profile Drawer'
          message='Do you want to close without saving?'
          onCancel={() => setShowDrawerCloseWarning(false)}
          onContinue={() => {
            setShowDrawerCloseWarning(false);
            closeEditProfileDrawer();
          }}
        />
      }

      {/* Edit Profile Drawer */}
      {showEditProfileDrawer &&
        <Drawer
          placement="right"
          visible={showEditProfileDrawer}
          toggleDrawer={closeEditProfileDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          {/* Drawer Container */}
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-user' />
                <div>Edit Patient Profile</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowDrawerCloseWarning(true)}
              />
            </div>
            {/* Content */}
            <div className={css(styles.sideDrawerContentArea)}>
              {/* Patient First, Middle and Last Name */}
              <div className='fx'>
                {/* Patient First Name */}
                <div>
                  <TextInputWithLabel
                    value={patientFirstName}
                    titleText="First Name"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={115}
                    onChange={handleOnEditPatientFirstName}
                  />
                </div>
                {/* Patient Middle Name */}
                <div className='ml-2'>
                  <TextInputWithLabel
                    value={patientMiddleName}
                    titleText="Middle Name"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={113}
                    onChange={handleOnEditPatientMiddleName}
                  />
                </div>
                {/* Patient Last Name */}
                <div className='ml-2'>
                  <TextInputWithLabel
                    value={patientLastName}
                    titleText="Last Name"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={114}
                    onChange={handleOnEditPatientLastName}
                  />
                </div>
              </div>
              {/* Patient SSN and DOB */}
              <div className='fx'>
                {/* SSN & DOB */}
                <div className='mt-4'>
                  <div className={css(styles.sectionHeading)}>SS#</div>
                  <div
                    className='fx'
                    style={{
                      alignItems: 'flex-end',
                      marginTop: '-10px'
                    }}
                  >
                    <div className='fx'>
                      <TextInputWithLabel
                        value={patientSSN1}
                        titleText=''
                        placeholder='???'
                        titleFontSize={12}
                        fontSize={12}
                        isCompactLayout={false}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={26}
                        onChange={handleOnEditPatientSSN1}
                      />
                      <div className={css(styles.ssnSeparator)}>-</div>
                      <TextInputWithLabel
                        value={patientSSN2}
                        titleText=''
                        placeholder='??'
                        titleFontSize={12}
                        fontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={26}
                        onChange={handleOnEditPatientSSN2}
                      />
                      <div className={css(styles.ssnSeparator)}>-</div>
                      <TextInputWithLabel
                        value={patientSSN3}
                        titleText=''
                        placeholder='????'
                        titleFontSize={12}
                        fontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={35}
                        onChange={handleOnEditPatientSSN3}
                      />
                    </div>
                    {/* DOB */}
                    <div className='fx' style={{ marginLeft: '40px' }}>
                      <i className='mr-2 fa fa-calendar' style={{ marginTop: '2px' }} />
                      <div className={`pr-2 ${css(styles.sectionHeading)}`}>
                        Date of Birth
                      </div>
                      <DatePicker
                        left={0}
                        right={-10}
                        date={patientDobDate}
                        month={patientDobMonth}
                        year={patientDobYear}
                        isPrisitine={isPatientDobPristine}
                        color={showPatientDobAlert ? salmon[500] : blue[400]}
                        noUnderline
                        onChangeDate={(year, month, date) => {
                          setShowPatientDobAlert(false);
                          setPatientDobDate(date);
                          setPatientDobMonth(month);
                          setPatientDobYear(year);
                          setIsPatientDobPristine(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Patient card */}
              <div className="fx mt-4">
                {/* Avatar */}
                <div className={css(styles.sideDrawerUserCardAvatar)}>
                  <img
                    src={patientAvatarImageUrl}
                    ref={drawerAvatarRef}
                    alt="avatar"
                    className={css(styles.sideDrawerUserCardAvatarImg)}
                    onClick={() => togglePatientAvatarCropperDrawer(true)}
                  />
                  <div
                    onClick={() => { togglePatientAvatarCropperDrawer(true); }}
                    className={css(styles.sideDrawerAvatarCamera)}
                  >
                    <i className="fa fa-camera" />
                  </div>
                </div>
                {/* User card displayName & userName */}
                <div className={`ml-2 ${css(styles.sideDrawerUserCardText)}`}>
                  {/* Display Name */}
                  <div style={{ marginTop: '-16px' }}>
                    <TextInputWithLabel
                      value={patientDisplayName}
                      titleText="Display Name (2 to 40 characters)*"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={268}
                      onChange={handleOnEditPatientDisplayName}
                    />
                    {/* Error message */}
                    {!isPatientDisplayNameValid && !isPatientDisplayNamePristine
                      ? <div className={css(styles.errMsg)}>
                        Invalid Display Name
                      </div>
                      : null
                    }
                  </div>
                  {/* UserName */}
                  <div
                    style={{
                      marginTop: (!isPatientDisplayNameValid && !isPatientDisplayNamePristine) ? '0px' : '4px'
                    }}
                  >
                    <TextInputWithLabel
                      value={patientUserName}
                      titleText="Username (2 to 20 characters, no spaces) *"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor={blue[500]}
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={268}
                      onChange={handleOnEditPatientUserName}
                    />
                    {/* Error message */}
                    {!isPatientUserNameValid && !isPatientUserNamePristine
                      ? <div className={css(styles.errMsg)}>
                        Invalid User Name
                      </div>
                      : null
                    }
                    {isPatientUserNameValid && !isPatientUserNamePristine && isPatientUserNameDuplicate
                      ? <div className={css(styles.errMsg)}>
                        User Name Taken
                      </div>
                      : null
                    }
                  </div>
                </div>
              </div>
              {/* Home Address */}
              <div className='mt-4'>
                {/* Heading */}
                <div className="fx fx__v-center" style={{ color: '#96c8ff' }}>
                  <i className="fa fa-home" style={{ fontSize: '18px' }} />
                  <div className="ml-2" style={{ fontSize: '14px', fontWeight: 'bold' }} >
                    Home Address
                  </div>
                </div>
                {/* Address */}
                <div className="fx">
                  <div>
                    <TextInputWithLabel
                      value={patientHomeStreetAddress}
                      titleText="Street"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={304}
                      placeholder='Required'
                      onChange={handleOnEditPatientHomeStreetAddress}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={patientHomeSuite}
                      titleText="Suite"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditPatientHomeSuite}
                    />
                  </div>
                </div>
                {/* City, State and Zip */}
                <div className="fx mt-2">
                  <div>
                    <TextInputWithLabel
                      value={patientHomeCity}
                      titleText="City"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={206}
                      placeholder='Required'
                      onChange={handleOnEditPatientHomeCity}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={patientHomeState}
                      titleText="State"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={65}
                      placeholder='Required'
                      onChange={handleOnEditPatientHomeState}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={patientHomeZip}
                      titleText="Zip"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={68}
                      placeholder='Required'
                      onChange={handleOnEditPatientHomeZip}
                    />
                  </div>
                </div>
              </div>
              {/* Mailing Address */}
              <div className='mt-4'>
                {/* Heading */}
                <div className="fx fx__v-center" style={{ color: '#96c8ff' }} >
                  <i className="fa fa-envelope" style={{ fontSize: '18px' }} />
                  <div className="ml-2" style={{ fontSize: '14px', fontWeight: 'bold' }} >
                    Mailing Address
                  </div>
                  <div style={{ marginLeft: '50px', fontSize: '14px', color: '#96C8FF' }} >
                    <Checkbox
                      text='Same as Home Address'
                      isChecked={isPatientMailingAddressSameAsHomeAddress}
                      onChange={() => {
                        setIsPatientMailingAddressSameAsHomeAddress(!isPatientMailingAddressSameAsHomeAddress);
                      }}
                    />
                  </div>
                </div>
                {/* Address */}
                {!isPatientMailingAddressSameAsHomeAddress &&
                  <div className="fx">
                    <div>
                      <TextInputWithLabel
                        value={patientMailingStreetAddress}
                        titleText="Street"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={304}
                        placeholder='Required'
                        onChange={handleOnEditPatientMailingStreetAddress}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={patientMailingSuite}
                        titleText="Suite"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={45}
                        onChange={handleOnEditPatientMailingSuite}
                      />
                    </div>
                  </div>
                }
                {/* City, State and Zip */}
                {!isPatientMailingAddressSameAsHomeAddress &&
                  <div className="fx mt-2">
                    <div>
                      <TextInputWithLabel
                        value={patientMailingCity}
                        titleText="City"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={206}
                        placeholder='Required'
                        onChange={handleOnEditPatientMailingCity}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={patientMailingState}
                        titleText="State"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={65}
                        placeholder='Required'
                        onChange={handleOnEditPatientMailingState}
                      />
                    </div>
                    <div className="ml-2">
                      <TextInputWithLabel
                        value={patientMailingZip}
                        titleText="Zip"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={68}
                        placeholder='Required'
                        onChange={handleOnEditPatientMailingZip}
                      />
                    </div>
                  </div>
                }
              </div>
              {/* Contact # (Mobile or Phone) */}
              <div
                className='fx mt-4'
                style={{
                  position: 'relative',
                  padding: '0'
                }}
              >
                <div>
                  <TextInputWithLabel
                    value={patientMobile}
                    titleText="Contact #"
                    isCompactLayout={false}
                    showPreIcon
                    preIcon={`fa fa-lg ${patientContactType.toLowerCase() === 'mobile' ? 'fa-mobile' : 'fa-phone'}`}
                    titleColor="#60A0E0"
                    textColor="#0080FF"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={174}
                    placeholder='Required'
                    onChange={handleOnEditPatientMobile}
                  />
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    position: 'relative',
                    left: '-90px',
                    color: blue[500]
                  }}
                  ref={contactRef}
                  onClick={() => setIsContactMenuOpen(true)}
                >
                  {patientContactType.toLowerCase() === 'mobile' ? 'Mobile' : 'Landline'}&nbsp;&nbsp;<i style={{ fontSize: '14px' }} className='fa fa-caret-down' />
                </div>
                <PopupMenu
                  top={20}
                  left={84}
                  visible={isContactMenuOpen}
                  parent={contactRef}
                  position="absolute"
                  onOutsideClick={() => { setIsContactMenuOpen(false); }}
                >
                  <div
                    className={css(styles.popupMenuContainer)}
                    style={{ width: '110px' }}
                  >
                    {/* Mobile */}
                    <div
                      className={`${patientContactType === 'mobile' ? 'selected' : ''} ${css(styles.popupMenuItemSmall, patientContactType === 'mobile' && styles.selectedPopupMenuItemSmall)}`}
                      onClick={() => {
                        handleOnChangePatientContactType('mobile');
                        setIsContactMenuOpen(false);
                      }}
                    >
                      <div className={css(styles.menuItemIconContainerSmall)}>
                        <i className={`fa fa-mobile ${css(styles.menuItemIconSmall)}`} />
                      </div>
                      <div>Mobile</div>
                    </div>
                    {/* Landline */}
                    <div
                      className={`${patientContactType === 'landline' ? 'selected' : ''} ${css(styles.popupMenuItemSmall, patientContactType === 'landline' && styles.selectedPopupMenuItemSmall)}`}
                      onClick={() => {
                        handleOnChangePatientContactType('landline');
                        setIsContactMenuOpen(false);
                      }}
                    >
                      <div className={css(styles.menuItemIconContainerSmall)}>
                        <i className={`fa fa-phone ${css(styles.menuItemIconSmall)}`} />
                      </div>
                      <div>Landline</div>
                    </div>
                  </div>
                </PopupMenu>
                {/* <div className='ml-2'>
                  <TextInputWithLabel
                    value={patientPhone}
                    titleText="Phone #"
                    isCompactLayout={false}
                    showPreIcon
                    preIcon='fa fa-phone'
                    titleColor="#60A0E0"
                    textColor="#0080FF"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={174}
                    onChange={handleOnEditPatientPhone}
                  />
                </div> */}
              </div>
              {/* Email */}
              <div className='mt-4'>
                <TextInputWithLabel
                  value={patientEmail}
                  titleText="Email"
                  isCompactLayout={false}
                  showPreIcon
                  preIcon='fa fa-envelope'
                  titleColor="#60A0E0"
                  textColor="#0080FF"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={356}
                  placeholder='Required'
                  onChange={handleOnEditPatientEmail}
                />
              </div>
              {/* Patient ID - Front */}
              <div className='mt-4'>
                <div className='fx'>
                  {idIcon}
                  <div className='ml-2'>
                    Patient ID (Front)
                  </div>
                </div>
                {showFrontIdLoader
                  ? <Loader iconType="cog" iconSize="5x" text="Uploading Patient ID(Front)" />
                  : <div style={{ position: 'relative' }}>
                    <img
                      className='mt-2'
                      onClick={() => toggleFrontCropperDrawer(true)}
                      ref={frontCardRef}
                      src={patientFrontIdImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'}
                      style={{
                        width: '343px',
                        maxWidth: '343px',
                        height: 'auto'
                      }}
                    />
                    {/* Camera Icon */}
                    <div
                      onClick={() => toggleFrontCropperDrawer(true)}
                      className={css(styles.drawerBannerCameraIcon)}
                    >
                      <i className="fa fa-camera" />
                    </div>
                  </div>
                }
              </div>
              {/* Patient ID - Back */}
              <div className='mt-4 mb-2'>
                <div className='fx'>
                  {idIcon}
                  <div className='ml-2'>
                    Patient ID (Back)
                  </div>
                </div>
                {showBackIdLoader
                  ? <Loader iconType="cog" iconSize="5x" text="Uploading Patient ID(Back)" />
                  : <div style={{ position: 'relative' }}>
                    <img
                      className='mt-2'
                      ref={backCardRef}
                      src={patientBackIdImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'}
                      style={{
                        width: '343px',
                        maxWidth: '343px',
                        height: 'auto'
                      }}
                    />
                    <div
                      onClick={() => toggleBackCropperDrawer(true)}
                      className={css(styles.drawerBannerCameraIcon)}
                    >
                      <i className="fa fa-camera" />
                    </div>
                  </div>
                }
              </div>
              <div style={{ height: '50px' }} />
            </div>
            {/* Drawer Footer */}
            <div
              className={`fx ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0', alignItems: 'center', justifyContent: 'flex-end' }}
            >
              {/* Cancel Button */}
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                isOutlineBtn
                onClick={() => setShowDrawerCloseWarning(true)}
                style={{
                  height: '32px',
                  width: '85px',
                  padding: '3px 0'
                }}
              >
                Cancel
              </Button>
              {/* Accept Button */}
              <Button
                btnType="primary"
                isDisabled={!canUpdatePatient || showFrontIdLoader || showBackIdLoader}
                className="ml-2 fx fx__hv-center"
                onClick={handleOnClickSavePatientProfile}
                style={{
                  height: '32px',
                  width: '85px',
                  padding: '3px 0'
                }}
              >
                Accept
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Patient Avatar Cropper Drawer */}
      {showPatientAvatarCropperDrawer &&
        <Drawer
          placement="right"
          visible={showPatientAvatarCropperDrawer}
          toggleDrawer={closePatientAvatarCropperDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Patient Avatar</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={closePatientAvatarCropperDrawer}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showPatientAvatarCropper
                ? <div>
                  <Cropper
                    src={patientAvatarImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    aspectRatio={1}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={patientAvatarCropperRef}
                  />
                </div>
                : null
              }
              {!showPatientAvatarCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={patientAvatarFileInputRef}
                    onDrop={handleOnPatientAvatarFileDrop}
                    onChange={handleOnPatientAvatarFileSelected}
                  />
                </div>
                : null
              }
              {isPatientAvatarUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Patient Avatar Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickPatientAvatarCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Avatar Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Patient ID Card (Front) Cropper Drawer */}
      {showFrontCropperDrawer &&
        <Drawer
          placement="right"
          visible={showFrontCropperDrawer}
          toggleDrawer={closeFrontCropperDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Patient ID (Front)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={closeFrontCropperDrawer}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showFrontCropper
                ? <div>
                  <Cropper
                    src={patientFrontIdImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={frontCropperRef}
                  />
                </div>
                : null
              }
              {!showFrontCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={frontFileInputRef}
                    onDrop={handleOnFrontFileDrop}
                    onChange={handleOnFrontFileSelected}
                  />
                </div>
                : null
              }
              {isFrontUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Patient ID (Front) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickFrontCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Patient ID Card (Back) Cropper Drawer */}
      {showBackCropperDrawer &&
        <Drawer
          placement="right"
          visible={showBackCropperDrawer}
          toggleDrawer={closeBackCropperDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Patient ID (Back)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={closeBackCropperDrawer}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showBackCropper
                ? <div>
                  <Cropper
                    src={patientBackIdImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={backCropperRef}
                  />
                </div>
                : null
              }
              {!showBackCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={backFileInputRef}
                    onDrop={handleOnBackFileDrop}
                    onChange={handleOnBackFileSelected}
                  />
                </div>
                : null
              }
              {isBackUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Patient ID (Back) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickBackCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Split Panel */}
      <div className={css(styles.splitPanel)}>
        {/* Panel Drawer */}
        <div className={css(styles.drawer, isDrawerOpen ? styles.openDrawer : styles.hideDrawer)}>
          {/* Drawer header (Mini patient card) */}
          <div
            className={`activeitem ${css(styles.drawerHeader)} ${css(styles.drawerHeaderActive)}`}
            onClick={handleOnClickMiniUserCard}
          >
            {/* Header icon */}
            <div className={css(styles.drawerHeaderIconContainer)}>
              <i className={`fa fa-user-o ${css(styles.drawerHeaderIcon)}`} />
            </div>
            {/* Header text */}
            <div className={css(styles.drawerHeaderTextContainer)}>
              <div className={css(styles.drawerHeaderText)}>
                {activePatient.displayName || ''}
              </div>
              <div className={css(styles.drawerHeaderTextSub)}>
                @{activePatient.userName}
              </div>
            </div>
          </div>
          {/* Drawer Tabs - Empty for now */}
          <div className={css(styles.drawerTabsContainer)} />
        </div>
        {/* Panel details */}
        <div
          className={css(styles.detail)}
          style={{
            width: isDrawerOpen ? `calc(100vw - ${drawerWidth}px)` : '100vw'
          }}
        >
          {/* Details area header */}
          <div className={css(styles.detailHeader)}>
            <div
              onClick={() => toggleDrawer(!isDrawerOpen)}
              style={{
                width: '16px',
                height: '24px',
                background: '#004080',
                position: 'relative',
                top: '10px',
                paddingLeft: '4px'
              }}
            >
              <i
                className={`mr-2 fa ${isDrawerOpen ? 'fa-caret-left' : 'fa-caret-right'}`}
                style={{ fontSize: '18px', color: blue[500] }}
              />
            </div>
            <div
              className='pl-4'
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <img
                src={activePatient.avatar || 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg'}
                alt='Patient Avatar'
                style={{
                  width: '80px',
                  height: '80px',
                  maxHeight: '80px'
                }}
              />
              <div style={{ flexGrow: '1', paddingLeft: '8px', height: '80px', lineHeight: '16px' }}>
                <div>
                  {activePatient.displayName && activePatient.displayName.length && activePatient.displayName.length > 0 ? titleCase(activePatient.displayName) : ''}
                </div>
                <div>@{activePatient.userName}</div>
              </div>
              <div style={{ height: '80px' }}>
                <div
                  ref={popupRef}
                  onClick={() => togglePopupMenu(true)}
                  style={{
                    height: '24px',
                    width: '24px',
                    background: ' #60A0E0',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#001824',
                    position: 'relative'
                  }}
                >
                  <i className='fa fa-ellipsis-v' />
                  <PopupMenu
                    top={28}
                    right={1}
                    visible={isPopupMenuOpen}
                    parent={popupRef}
                    position="absolute"
                    onOutsideClick={() => { togglePopupMenu(false); }}
                  >
                    <div className={css(styles.popupMenuContainer)}>
                      {/* Edit Profile */}
                      <div
                        className={`m-item ${css(styles.popupMenuItem)}`}
                        onClick={openPatientEditProfileDrawer}
                      >
                        <div className={css(styles.menuItemIconContainer)}>
                          <i className={`fa fa-pencil ${css(styles.menuItemIcon)}`} />
                        </div>
                        <div>Edit Profile</div>
                      </div>
                    </div>
                  </PopupMenu>
                </div>
              </div>
            </div>
          </div>
          {/* Active Component - Tabs */}
          <div
            ref={mainTabsContainer}
            style={{
              height: '100%',
              flexGrow: '1',
              flexShrink: '1',
              display: 'flex',
              flexFlow: 'column',
              overflowY: 'hidden',
              paddingLeft: '8px'
            }}
          >
            <TabsContainer
              dropDownId="d1"
              showPlusIcon={false}
              isDrawer={false}
              shiftMethod='availableWidth'
              containerWidth={mainTabsContainer && mainTabsContainer.current ? mainTabsContainer.current.getBoundingClientRect().width : 610}
              otherElementsWidth={320}
              minWidth={610}
              dropDownTop={39}
              dropDownLeft={44}
              containerStyles={{ width: '100%' }}
            >
              {/* Info */}
              <TabItem
                key='about_tab'
                tabKey="1"
                parentKey="0111"
                text="About"
                isActive={pathname.endsWith('/info')}
                showSvgIcon
                svgIcon={patientInfoFlag ? flagIcon : userIcon}
                style={{
                  width: '70px',
                  minWidth: '70px',
                  maxWidth: '70px'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  history.push(`${url}/info`);
                }}
              >
                <Link
                  to={`${url}/info`}
                  className={css(
                    styles.tabText,
                    styles.linkStyles,
                    pathname === '/patient/info' && styles.selectedLink
                  )}
                >
                  Info
                </Link>
              </TabItem>
              {/* Insurance */}
              <TabItem
                key='insurance_tab'
                tabKey="3"
                parentKey="0333"
                text="Insurance"
                isActive={
                  pathname.endsWith('/insurance/primary') ||
                  pathname.endsWith('/insurance/secondary') ||
                  pathname.endsWith('/insurance/tertiary') ||
                  pathname.endsWith('/insurance/selfpay')
                }
                showSvgIcon
                svgIcon={(piFlag || siFlag || tiFlag) ? flagIcon : insuranceIcon}
                style={{
                  width: '112px',
                  minWidth: '112px',
                  maxWidth: '112px'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  history.push(`${url}/insurance/primary`);
                }}
              >
                <Link
                  to={`${url}/insurance`}
                  className={css(
                    styles.tabText,
                    styles.linkStyles,
                    pathname === '/user/about' && styles.selectedLink
                  )}
                >
                  Insurance
                </Link>
              </TabItem>
              {/* Contact */}
              <TabItem
                key='contact_tab'
                tabKey="4"
                parentKey="0444"
                text="Contact"
                isActive={pathname.endsWith('/contact')}
                showSvgIcon
                svgIcon={contactPersonFlag ? flagIcon : contactIcon}
                style={{
                  width: '98px',
                  minWidth: '98px',
                  maxWidth: '98px'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  history.push(`${url}/contact`);
                }}
              >
                <Link
                  to={`${url}/contact`}
                  className={css(
                    styles.tabText,
                    styles.linkStyles,
                    pathname === '/user/about' && styles.selectedLink
                  )}
                >
                  Contact
                </Link>
              </TabItem>
            </TabsContainer>
            {/* Nested components */}
            <Switch>
              <Route exact path={`${path}`}>
                <PatientInfo patientData={patientData} />
              </Route>
              <Route path={`${path}/info`}>
                <PatientInfo patientData={patientData} />
              </Route>
              <Route path={`${path}/insurance`}>
                <PatientInsurance
                  piFlag={piFlag}
                  siFlag={siFlag}
                  tiFlag={tiFlag}
                  updateFlag={handleOnUpdateFlag}
                  patientData={{
                    firstName: activePatient.firstName,
                    middleName: activePatient.middleName,
                    lastName: activePatient.lastName,
                    dob: activePatient.dob,
                    isNoPrimaryInsurance: activePatient.isNoPrimaryInsurance,
                    isNoSecondaryInsurance: activePatient.isNoSecondaryInsurance,
                    isNoTertiaryInsurance: activePatient.isNoTertiaryInsurance
                  }}
                />
              </Route>
              <Route path={`${path}/contact`}>
                <PatientContact
                  patientData={activePatient}
                  updateFlag={handleOnUpdateFlag}
                />
              </Route>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;
